import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import { listStaff } from "../api/staffs";
import { getWalkinQueueReportSummary } from "../api/walkinQueue";
import { getCurrentSalonId } from "../utils/currentSalon";
import { getSalonTimezone } from "../utils/salonTimezone";
import { formatDateTimeInTimeZone } from "../utils/datetime";
import { getReportsHomePathForRoute } from "../utils/reportRouteContext";
import { formatPermissionError } from "../utils/permissionErrorMessages";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./ReportsSectionCompact.css";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fallback
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const formatMoney = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `${(num * 100).toFixed(1)}%`;
};

const HELP_GUIDE = {
  title: "Help Guide: Walk-In Queue Report",
  subtitle: "Review walk-in assignments, fairness, and revenue by staff.",
  sections: [
    {
      id: "overview",
      title: "Overview",
      description: "Understand walk-in volume, fairness, and revenue trends.",
      anchorId: "walkinq-overview",
      bullets: [
        "Use this report to review queue performance for a date range.",
        "Scan fairness deltas to keep rotation balanced.",
      ],
      tips: ["Compare similar date ranges for a clean read."],
    },
    {
      id: "filters",
      title: "Filters",
      description: "Set the date range, staff, and queue mode filters.",
      anchorId: "walkinq-filters",
      bullets: [
        "Start and end dates follow salon timezone.",
        "Filter staff to inspect individual fairness.",
      ],
      tips: ["Use mode filters to isolate strict vs weighted rotation."],
    },
    {
      id: "kpis",
      title: "KPIs",
      description: "Review walk-ins, revenue, overrides, and auto-skips.",
      anchorId: "walkinq-kpis",
      bullets: ["KPIs update when you refresh the report."],
      tips: [],
    },
    {
      id: "by-staff",
      title: "Walk-ins by staff",
      description: "See assignments, revenue, and fairness share by staff.",
      anchorId: "walkinq-by-staff",
      bullets: [
        "Share shows how assignments are distributed.",
        "Fairness delta highlights who is ahead or behind.",
      ],
      tips: ["Filter by staff for focused reviews."],
    },
    {
      id: "overrides",
      title: "Overrides",
      description: "Track manual overrides that changed the suggested staff.",
      anchorId: "walkinq-overrides",
      bullets: ["Review reasons to spot recurring exceptions."],
      tips: [],
    },
    {
      id: "auto-skips",
      title: "Auto-skips",
      description: "See automatic skips and their counts by reason.",
      anchorId: "walkinq-auto-skips",
      bullets: ["Use this to identify availability or timing issues."],
      tips: [],
    },
    {
      id: "export",
      title: "Export",
      description: "Download a CSV of the walk-in by staff table.",
      anchorId: "walkinq-export",
      bullets: ["Use the export for deeper analysis."],
      tips: [],
    },
  ],
  troubleshooting: [
    { title: "No walk-ins showing", body: "Check date range and filters." },
    { title: "Export fails", body: "Refresh the report and try again." },
  ],
};

export default function WalkInQueueReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const salonId = getCurrentSalonId(user);
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );

  const [startDate, setStartDate] = useState(
    formatDateISO(addDays(new Date(), -6), salonTimezone)
  );
  const [endDate, setEndDate] = useState(
    formatDateISO(new Date(), salonTimezone)
  );
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffFilter, setStaffFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      navigate("/login?next=/manager/reports/walk-in-queue", { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!salonId) return;
    listStaff({ salon_id: salonId }, token)
      .then((data) => setStaffOptions(Array.isArray(data) ? data : []))
      .catch(() => setStaffOptions([]));
  }, [salonId, token]);

  const fetchSummary = useCallback(async () => {
    if (!salonId || !token) return;
    setLoading(true);
    setError("");
    try {
      const params = {
        from_date: startDate || null,
        to_date: endDate || null,
      };
      if (modeFilter !== "all") {
        params.mode = modeFilter;
      }
      const data = await getWalkinQueueReportSummary(salonId, params, token);
      setSummary(data || null);
    } catch (err) {
      setError(
        formatPermissionError(err, "Unable to load walk-in queue report.")
      );
    } finally {
      setLoading(false);
    }
  }, [salonId, token, startDate, endDate, modeFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const staffById = useMemo(() => {
    const map = new Map();
    staffOptions.forEach((staff) => {
      map.set(String(staff.id), staff);
    });
    return map;
  }, [staffOptions]);

  const totalAssignments = summary?.total_walkins || 0;

  const byStaffRows = useMemo(() => {
    const rows = summary?.by_staff || [];
    const fairness = summary?.fairness || [];
    const fairnessMap = new Map(
      fairness.map((entry) => [String(entry.staff_id), entry])
    );
    return rows
      .filter((row) =>
        staffFilter ? String(row.staff_id) === String(staffFilter) : true
      )
      .map((row) => {
        const staff = staffById.get(String(row.staff_id));
        const fairnessEntry = fairnessMap.get(String(row.staff_id));
        return {
          ...row,
          staff_name:
            staff?.display_name || staff?.name || `Staff #${row.staff_id}`,
          fairness: fairnessEntry,
          share:
            totalAssignments > 0 ? row.assignments / totalAssignments : 0,
          avg_ticket:
            row.assignments > 0 ? row.revenue / row.assignments : 0,
        };
      });
  }, [summary, staffFilter, staffById, totalAssignments]);

  const overridesRows = useMemo(() => {
    const rows = summary?.overrides || [];
    return rows.filter((row) =>
      staffFilter ? String(row.staff_id) === String(staffFilter) : true
    );
  }, [summary, staffFilter]);

  const autoSkipsRows = summary?.auto_skips || [];

  const revenueTotal = byStaffRows.reduce(
    (total, row) => total + (Number(row.revenue) || 0),
    0
  );

  const exportCsv = () => {
    const headers = [
      "Staff",
      "Walk-ins",
      "Revenue",
      "Avg Ticket",
      "Share",
      "Fairness Delta",
    ];
    const rows = byStaffRows.map((row) => [
      row.staff_name,
      row.assignments,
      row.revenue,
      row.avg_ticket,
      row.share,
      row.fairness?.delta ?? 0,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "")}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "walkin-queue-report.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Walk-In Queue Report
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6 text-base md:text-lg">
            <div id="walkinq-overview" className={getSectionClassName("walkinq-overview")}>
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div
              id="walkinq-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                "walkinq-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Start date
                  </div>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    End date
                  </div>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Staff
                  </div>
                  <select
                    value={staffFilter}
                    onChange={(event) => setStaffFilter(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">All staff</option>
                    {staffOptions.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.display_name || staff.name || `Staff #${staff.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Mode
                  </div>
                  <select
                    value={modeFilter}
                    onChange={(event) => setModeFilter(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="all">All modes</option>
                    <option value="strict_rotation">Strict</option>
                    <option value="weighted_rotation">Weighted</option>
                    <option value="manual_suggested_next">Manual</option>
                  </select>
                </div>
              </div>
              <div
                id="walkinq-export"
                className={`mt-3 flex items-center justify-between ${getSectionClassName(
                  "walkinq-export"
                )}`}
              >
                <button
                  type="button"
                  onClick={fetchSummary}
                  className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={exportCsv}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Export CSV
                </button>
              </div>
            </div>

            <div
              id="walkinq-kpis"
              className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${getSectionClassName(
                "walkinq-kpis"
              )}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase text-slate-500">Walk-ins</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {loading ? "--" : totalAssignments}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase text-slate-500">Revenue</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {loading ? "--" : formatMoney(revenueTotal)}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase text-slate-500">Overrides</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {loading ? "--" : overridesRows.length}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase text-slate-500">Auto-skips</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {loading
                    ? "--"
                    : autoSkipsRows.reduce((sum, row) => sum + (row.count || 0), 0)}
                </div>
              </div>
            </div>

            <div
              id="walkinq-by-staff"
              className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${getSectionClassName(
                "walkinq-by-staff"
              )}`}
            >
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="text-sm font-semibold text-slate-900">
                  Walk-ins by Staff
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left">Staff</th>
                      <th className="px-4 py-3 text-right">Walk-ins</th>
                      <th className="px-4 py-3 text-right">Revenue</th>
                      <th className="px-4 py-3 text-right">Avg ticket</th>
                      <th className="px-4 py-3 text-right">Share</th>
                      <th className="px-4 py-3 text-right">Fairness delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byStaffRows.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-6 text-center text-slate-500">
                          No walk-ins found for this range.
                        </td>
                      </tr>
                    )}
                    {byStaffRows.map((row) => (
                      <tr key={row.staff_id} className="border-t border-slate-200">
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {row.staff_name}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {row.assignments}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {formatMoney(row.revenue)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {formatMoney(row.avg_ticket)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {formatPercent(row.share)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {row.fairness ? formatPercent(row.fairness.delta) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div
                id="walkinq-overrides"
                className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${getSectionClassName(
                  "walkinq-overrides"
                )}`}
              >
                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="text-sm font-semibold text-slate-900">
                    Overrides
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-left">Assigned</th>
                        <th className="px-4 py-3 text-left">Suggested</th>
                        <th className="px-4 py-3 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overridesRows.length === 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-6 text-center text-slate-500">
                            No overrides in this range.
                          </td>
                        </tr>
                      )}
                      {overridesRows.map((row, index) => (
                        <tr key={`${row.staff_id}-${index}`} className="border-t border-slate-200">
                          <td className="px-4 py-3 text-slate-700">
                            {formatDateTimeInTimeZone(row.timestamp, salonTimezone)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {staffById.get(String(row.staff_id))?.display_name ||
                              staffById.get(String(row.staff_id))?.name ||
                              `Staff #${row.staff_id || ""}`}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {staffById.get(String(row.suggested_staff_id))?.display_name ||
                              staffById.get(String(row.suggested_staff_id))?.name ||
                              (row.suggested_staff_id
                                ? `Staff #${row.suggested_staff_id}`
                                : "-")}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.reason || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div
                id="walkinq-auto-skips"
                className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${getSectionClassName(
                  "walkinq-auto-skips"
                )}`}
              >
                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="text-sm font-semibold text-slate-900">
                    Auto-skips
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left">Reason</th>
                        <th className="px-4 py-3 text-right">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoSkipsRows.length === 0 && (
                        <tr>
                          <td colSpan="2" className="px-4 py-6 text-center text-slate-500">
                            No auto-skips in this range.
                          </td>
                        </tr>
                      )}
                      {autoSkipsRows.map((row, index) => (
                        <tr key={`${row.reason}-${index}`} className="border-t border-slate-200">
                          <td className="px-4 py-3 text-slate-700">{row.reason}</td>
                          <td className="px-4 py-3 text-right text-slate-700">
                            {row.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
